import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { type Status } from './badge';
import { Icon, type IconName } from './icon';

const tones: Record<Status, { classes: string; icon: IconName }> = {
  info: { classes: 'border-info-line bg-info-subtle', icon: 'info' },
  success: { classes: 'border-success-line bg-success-subtle', icon: 'circle-check' },
  warning: { classes: 'border-warning-line bg-warning-subtle', icon: 'triangle-alert' },
  danger: { classes: 'border-danger-line bg-danger-subtle', icon: 'circle-alert' },
  neutral: { classes: 'border-line bg-sunken', icon: 'info' },
};

const iconColors: Record<Status, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  neutral: 'text-fg-muted',
};

export interface BannerProps {
  tone?: Status;
  title?: ReactNode;
  children?: ReactNode;
  icon?: IconName;
  /** Action on the right, e.g. a small button. */
  action?: ReactNode;
  /** `bar`: full width without rounded corners, below the top bar of the shell. */
  layout?: 'inline' | 'bar';
  /** `alert` for messages that appear while the person works and must be announced. */
  role?: 'status' | 'alert';
  className?: string;
}

/** Message about a whole page or area. Messages about a field stay at the field. */
export function Banner({
  tone = 'info',
  title,
  children,
  icon,
  action,
  layout = 'inline',
  role,
  className,
}: BannerProps) {
  return (
    <div
      role={role}
      className={cn(
        'flex items-start gap-3 border text-fg',
        tones[tone].classes,
        layout === 'inline' ? 'rounded-md px-3.5 py-3' : 'border-x-0 border-t-0 px-4 py-2',
        className,
      )}
    >
      <Icon name={icon ?? tones[tone].icon} className={cn('mt-0.5', iconColors[tone])} />
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cn(title && 'mt-0.5 text-fg-muted')}>{children}</div>}
      </div>
      {action && <div className="-my-1 shrink-0">{action}</div>}
    </div>
  );
}
